import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Download, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, getModules } from "@/lib/api";
import { formatCell } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader, Panel } from "@/components/PageHeader";
import { RecordDialog } from "@/components/RecordDialog";
import { Attachments } from "@/components/Attachments";
import { PrintButton } from "@/components/PrintButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const exportCsv = (mod, rows) => {
  const cols = mod.columns;
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${mod.title.replace(/\s+/g, "_")}.csv`;
  a.click();
};

export const GenericModule = ({ moduleKey, deptName }) => {
  const { user } = useAuth();
  const [mod, setMod] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState({ open: false, record: null });
  const [toDelete, setToDelete] = useState(null);
  const [attFor, setAttFor] = useState(null);

  const load = useCallback(() => api.get(`/m/${moduleKey}`).then((r) => setRows(r.data)), [moduleKey]);

  useEffect(() => {
    setMod(null);
    setQ("");
    setStatus("all");
    getModules().then((m) => setMod(m[moduleKey]));
    load();
  }, [moduleKey, load]);

  const statusField = mod?.fields.find((f) => f.name === "status");
  const filtered = useMemo(() => rows.filter((r) =>
    (status === "all" || r.status === status) &&
    (!q || Object.values(r).some((v) => String(v).toLowerCase().includes(q.toLowerCase())))), [rows, q, status]);

  if (!mod) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  const writable = canWrite(user, mod.dept);
  const fieldType = (c) => mod.fields.find((f) => f.name === c)?.type;
  const label = (c) => (c === "code" ? "ID" : mod.fields.find((f) => f.name === c)?.label || c);

  const del = async () => {
    try {
      await api.delete(`/m/${moduleKey}/${toDelete.id}`);
      toast.success(`${toDelete.code} deleted`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader dept={deptName} title={mod.title} subtitle={mod.description}>
        <Button variant="outline" onClick={() => exportCsv(mod, filtered)} data-testid="btn-export-csv"><Download className="mr-1.5 h-4 w-4" />Export</Button>
        {writable && <Button onClick={() => setDialog({ open: true, record: null })} data-testid="btn-new-record"><Plus className="mr-1.5 h-4 w-4" />New</Button>}
      </PageHeader>
      <Panel>
        <div className="flex flex-col gap-2 border-b border-slate-200 p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="input-search" placeholder="Search..." className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {statusField && (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="sm:w-48" data-testid="select-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statusField.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {mod.columns.map((c) => <TableHead key={c} className="whitespace-nowrap text-xs uppercase tracking-wider">{label(c)}</TableHead>)}
                {(writable || mod.attachments || mod.printable) && <TableHead className="text-right text-xs uppercase">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`row-${moduleKey}-${r.code}`} className="row-hover">
                  {mod.columns.map((c) => (
                    <TableCell key={c} className={c === "code" ? "whitespace-nowrap font-mono text-xs font-medium" : "text-sm"}>
                      {c === "status" ? <StatusBadge value={r.status} /> : formatCell(fieldType(c), r[c])}
                    </TableCell>
                  ))}
                  {(writable || mod.attachments || mod.printable) && (
                    <TableCell className="whitespace-nowrap text-right">
                      {mod.printable && <PrintButton path={`/print/${mod.printable}/${r.id}`} label="" size="icon" variant="ghost" testId={`btn-print-${r.code}`} />}
                      {mod.attachments && (
                        <Button size="sm" variant="ghost" onClick={() => setAttFor(r)} data-testid={`btn-attachments-${r.code}`}>
                          <Paperclip className="mr-1 h-4 w-4" />{r.attachment_count || 0}
                        </Button>
                      )}
                      {writable && <>
                      <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, record: r })} data-testid={`btn-edit-${r.code}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setToDelete(r)} data-testid={`btn-delete-${r.code}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                      </>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={mod.columns.length + 1} className="py-10 text-center text-sm text-slate-500" data-testid="empty-state">No records found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-500" data-testid="record-count">{filtered.length} of {rows.length} records</div>
      </Panel>
      <RecordDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} moduleKey={moduleKey} mod={mod} record={dialog.record} onSaved={load} />
      <Dialog open={!!attFor} onOpenChange={(o) => !o && setAttFor(null)}>
        <DialogContent className="p-0 sm:max-w-xl" data-testid="dialog-attachments">
          <DialogHeader className="px-4 pt-4"><DialogTitle className="font-heading">Attachments · {attFor?.code} {attFor?.doc_name}</DialogTitle></DialogHeader>
          {attFor && <Attachments entityType={moduleKey} entityId={attFor.id} writable={writable} defaultCategory="Signed Approval Note" onChange={load} />}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {toDelete?.code}?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="btn-confirm-delete" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
